/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/string/TrimAll.ts
 * @create:      2025-06-25 17:20:44.792
 * @modify:      2025-07-11 21:19:12.838
 * @version:     6.0.0
 * @times:       8
 * @lines:       83
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { TrimEnd } from "./TrimEnd";
import { TrimStart } from "./TrimStart";

/**
 * Removes all occurrences of the specified target substring from the input string.
 *
 * @param input - The original string to process.
 * @param target - The substring to remove from the input string.
 * @returns A new string with all instances of the target substring removed.
 * 
 * @remarks This function will remove all occurrences of the target substring from both the start and end of the input string.
 * 
 * @since 3.0.3
 * @version 2025-06-25
 */
export function TrimAll(input: string, target: string): string;
/**
 * Trims all occurrences of the specified target substring from the input string, up to a maximum number of times.
 *
 * @param input - The string to trim.
 * @param target - The substring to remove from the input string.
 * @param max - The maximum number of times to delete the `target` substring on both sides of the `input` string.
 * @returns The trimmed string with up to `max` occurrences of `target` removed.
 * 
 * @since 3.0.3
 * @version 2025-06-25
 */
export function TrimAll(input: string, target: string, max: number): string;
/**
 * Removes all occurrences of the specified target substrings from the input string.
 *
 * @param input - The original string to process.
 * @param targets - An array of substrings to be removed from the input string.
 * @returns The resulting string after all target substrings have been removed.
 * 
 * @since 3.0.3
 * @version 2025-06-25
 */
export function TrimAll(input: string, targets: string[]): string;
export function TrimAll(p1: string, p2: string | string[], p3?: number): string {
    if (Array.isArray(p2)) {
        return TrimEnd(TrimStart(p1, p2), p2);
    } else {
        if (p3 !== undefined) {
            return TrimEnd(TrimStart(p1, p2, p3), p2, p3);
        }

        return TrimEnd(TrimStart(p1, p2), p2);
    }
}
